import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import {
  DEFAULT_PARAMS,
  loadStoredStreamingParams,
} from 'features/kestrelViewer/ui/tools/StreamingParamsPanel'
import {
  KestrelGatekeeper,
  KVAnnotation,
  KVCameraPose,
  KVInspectionArea,
  KVMeasurement,
  KVNavigationMode,
  KVPlaneIntersection,
  KVPrimitive,
  KVUnitType,
  KVUserSettings,
} from 'types'

import {
  sendPointAnnotationsBatched,
  sendPolygonAnnotationsBatched,
} from './annotationBatchUtils'
import { emitPixelStreamUIInteraction } from './messageUtils'

type StartingView = {
  id: string
  cameraPosition: unknown
  cameraRotation: unknown
}

type OrientationAxesLike = {
  orientationVector: { x: number; y: number; z: number }
  orientationNameFront: string
  orientationNameBack: string
  orientationNameLeft: string
  orientationNameRight: string
  orientationNameUp: string
  orientationNameDown: string
}

type KestrelModelLike =
  | {
      id?: string
      data?: {
        sceneSettings?: {
          startingView?: StartingView | null
          orientationAxes?: OrientationAxesLike | null
        }
        annotations: KVAnnotation[]
        measurements: KVMeasurement[]
        planeIntersections: KVPlaneIntersection[]
        inspection_type?: string
        inspectionAreas?: KVInspectionArea[]
        cameraPosesScale: number
        primitives?: KVPrimitive[]
        measurementScale: number
      }
    }
  | null
  | undefined

export interface InitializationOptions {
  pixelStreaming: PixelStreaming
  kestrelModel: KestrelModelLike
  historicalAnnotations?: KVAnnotation[] | null
  cameraPoses?: KVCameraPose[] | null
  gatekeeper: KestrelGatekeeper
  hasDataLayersSupport?: boolean
  thermalGradientVisible: boolean
  navigationMode: KVNavigationMode
  selectedMode: string
  measurementType: string
  is2DPlaneModeActive: boolean
  currentUnit: KVUnitType
  settings?: KVUserSettings
  authToken?: string
  rolodexUrl?: string
  kestrelApiUrl?: string
  userEmail?: string
  userName?: string
  inspectionAreas?: KVInspectionArea[]
}

export function initializationCommands(options: InitializationOptions) {
  const {
    pixelStreaming,
    kestrelModel,
    historicalAnnotations,
    cameraPoses,
    gatekeeper,
    hasDataLayersSupport,
    thermalGradientVisible,
    navigationMode,
    selectedMode,
    measurementType,
    is2DPlaneModeActive,
    currentUnit,
    settings,
    authToken,
    rolodexUrl,
    kestrelApiUrl,
    userEmail,
    userName,
    inspectionAreas,
  } = options

  // Auth to Rolodex
  emitPixelStreamUIInteraction(pixelStreaming, {
    command: KestrelCommands.AUTHENTICATE_ROLODEX,
    token: authToken ?? '',
    rolodexUrl,
    userEmail,
    userName,
    kestrelModelEntryId: kestrelModel?.id ?? '',
  })

  // Auth to Kestrel API
  emitPixelStreamUIInteraction(pixelStreaming, {
    command: KestrelCommands.AUTHENTICATE_KESTREL_API,
    token: authToken ?? '',
    kestrelAPIUrl: kestrelApiUrl,
    userEmail,
    userName,
  })

  // Starting view
  const startingView: StartingView | undefined = (kestrelModel?.data
    ?.sceneSettings?.startingView ?? undefined) as StartingView | undefined
  if (startingView) {
    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.ZOOM_TO_STARTING_VIEW,
      cameraViewId: startingView.id,
      cameraViewPosition: startingView.cameraPosition,
      cameraViewRotation: startingView.cameraRotation,
    })
  }

  // Orientation axes
  const orientationAxes: OrientationAxesLike | undefined = (kestrelModel?.data
    ?.sceneSettings?.orientationAxes ?? undefined) as
    | OrientationAxesLike
    | undefined
  if (orientationAxes) {
    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.SET_ORIENTATION_AXES,
      orientationAxes,
    })
  }

  // Annotations and other model data
  if (kestrelModel?.data) {
    const allAnnotations: KVAnnotation[] = [
      ...(kestrelModel.data.annotations ?? []),
      ...((historicalAnnotations ?? []) as KVAnnotation[]),
    ]

    sendPointAnnotationsBatched(pixelStreaming, allAnnotations)

    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.LOAD_MEASUREMENTS,
      measurements: (kestrelModel.data.measurements ?? []).map((m) =>
        KVMeasurement.toJSON(m)
      ),
    })

    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.LOAD_PLANE_INTERSECTIONS,
      planeIntersections: (kestrelModel.data.planeIntersections ?? []).map(
        (pi) => KVPlaneIntersection.toJSON(pi)
      ),
    })

    sendPolygonAnnotationsBatched(pixelStreaming, allAnnotations)

    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.SET_MEASUREMENT_SCALE,
      measurementScale: kestrelModel.data.measurementScale,
    })

    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.LOAD_SURFACE_AREA_LABEL_SETTINGS,
      showLabel: gatekeeper.feature.labelVisible,
      showArea: gatekeeper.feature.areaVisible,
    })

    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.LOAD_INSPECTION_AREAS,
      inspectionAreas:
        (inspectionAreas ?? kestrelModel.data.inspectionAreas)?.map((ia) =>
          KVInspectionArea.toJSON(ia)
        ) ?? [],
    })

    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.SET_CAMERA_POSES_SCALE,
      cameraPosesScale: kestrelModel.data.cameraPosesScale,
    })

    // Send camera poses if available
    if (cameraPoses && cameraPoses.length > 0) {
      emitPixelStreamUIInteraction(pixelStreaming, {
        command: KestrelCommands.SET_CAMERA_POSES,
        cameraPoses: cameraPoses.map((pose) => KVCameraPose.toJSON(pose)),
        cameraPosesVisible: false, // Start hidden, user toggles visibility
      })
    }

    // Clear any leftover projection lines from previous session
    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.HIDE_CAMERA_POSE_PROJECTIONS,
    })

    // Clear camera poses visibility from previous session
    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.SET_CAMERA_POSES_VISIBILITY,
      cameraPosesVisible: false,
    })

    // Clear any highlighted camera pose from previous session
    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.HIGHLIGHT_CAMERA_POSE,
      highlight: false,
    })

    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.LOAD_PRIMITIVES,
      primitives:
        kestrelModel.data.primitives?.map((p) => KVPrimitive.toJSON(p)) ?? [],
    })
  }

  if (hasDataLayersSupport) {
    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.SET_THERMAL_GRADIENT_VISIBILITY,
      visible: thermalGradientVisible,
    })
  }

  emitPixelStreamUIInteraction(pixelStreaming, {
    command: KestrelCommands.SET_NAVIGATION_MODE,
    mode: navigationMode,
  })

  emitPixelStreamUIInteraction(pixelStreaming, {
    command: KestrelCommands.SET_MODE,
    mode: selectedMode,
  })

  emitPixelStreamUIInteraction(pixelStreaming, {
    command: KestrelCommands.SET_MEASUREMENT_TYPE,
    measurementType,
  })

  emitPixelStreamUIInteraction(pixelStreaming, {
    command: KestrelCommands.SET_2D_PLANE_MODE,
    active: is2DPlaneModeActive,
  })

  emitPixelStreamUIInteraction(pixelStreaming, {
    command: 'changeMeasurementUnit',
    measurementUnit: currentUnit.toLowerCase(),
  })

  emitPixelStreamUIInteraction(pixelStreaming, {
    command: 'setCompassVisibility',
    enabled: gatekeeper.feature.compassVisible,
  })

  emitPixelStreamUIInteraction(pixelStreaming, {
    command: KestrelCommands.SET_LABEL_CULLING_ENABLED,
    enabled: gatekeeper.feature.labelCullingEnabled,
  })

  if (settings) {
    emitPixelStreamUIInteraction(pixelStreaming, {
      command: KestrelCommands.UPDATE_SETTINGS,
      settings: {
        orbitSpeed: settings.orbitSpeed,
        fieldOfView: settings.fieldOfView,
        inputProfile: settings.inputProfile,
      },
    })
  }

  // Apply streaming params if they exist and differ from defaults
  const storedParams = loadStoredStreamingParams()
  if (storedParams) {
    const hasDifferences =
      storedParams.maxBitrate !== DEFAULT_PARAMS.maxBitrate ||
      storedParams.minBitrate !== DEFAULT_PARAMS.minBitrate ||
      storedParams.maxFps !== DEFAULT_PARAMS.maxFps ||
      storedParams.disableMotionBlur !== DEFAULT_PARAMS.disableMotionBlur ||
      storedParams.minQuality !== DEFAULT_PARAMS.minQuality

    if (hasDifferences) {
      const maxBitrateKbps =
        parseInt(storedParams.maxBitrate, 10) ||
        parseInt(DEFAULT_PARAMS.maxBitrate)
      const minBitrateKbps =
        parseInt(storedParams.minBitrate, 10) ||
        parseInt(DEFAULT_PARAMS.minBitrate)
      const maxFps =
        parseInt(storedParams.maxFps, 10) || parseInt(DEFAULT_PARAMS.maxFps)
      const minQuality = Math.min(
        100,
        Math.max(
          1,
          parseInt(storedParams.minQuality, 10) ||
            parseInt(DEFAULT_PARAMS.minQuality)
        )
      )

      emitPixelStreamUIInteraction(pixelStreaming, {
        command: KestrelCommands.SET_STREAMING_PARAMS,
        maxBitrate: maxBitrateKbps * 1000, // Convert kbps to bps
        minBitrate: minBitrateKbps * 1000, // Convert kbps to bps
        maxFps,
        disableMotionBlur: storedParams.disableMotionBlur,
        minQuality,
      })
    }
  }
}
